import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ProfileAchievement } from '../../types/profile';
import LeftRightNavigationButtons from '../common/LeftRightNavigationButtons';
import CollapsibleContent from '../common/CollapsibleContent';
import { resolveServiceCategoryLabel } from '../../utils/serviceCategoryLabel';

export function ProfileAchievementsSection({
  achievements,
}: {
  achievements?: ProfileAchievement[];
}) {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right

  const achievementsCount = achievements?.length ?? 0;
  const current = achievements?.[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < achievementsCount - 1;

  // Animation variants for swipe
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      position: 'absolute' as const,
    }),
    center: {
      x: 0,
      opacity: 1,
      position: 'relative' as const,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      position: 'absolute' as const,
    }),
  };

  const swipeTo = (newIndex: number) => {
    setDirection(newIndex > currentIndex ? 1 : -1);
    setCurrentIndex(newIndex);
  };

  return (
    <div className="border-2 relative mb-6 overflow-hidden rounded-2xl border-gray-300 bg-white px-6 py-4 shadow-xl">
      <CollapsibleContent
        header={
          <div className="mb-2 text-lg font-bold text-secondary">
            {t('achievements.title', { ns: 'common', defaultValue: 'Réalisations' })}
          </div>
        }
      >
        {achievementsCount > 0 && current ? (
          <div className="md:flex md:gap-8">
            <div className="relative mb-4 flex h-60 w-full items-center justify-center overflow-hidden rounded bg-gray-100 md:h-96 md:w-96 md:flex-shrink-0">
              <AnimatePresence custom={direction} initial={false}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: 'spring', stiffness: 300, damping: 30, duration: 0.4 }}
                  className="flex h-full w-full items-center justify-center"
                >
                  {current.project_image_url ? (
                    <img
                      src={current.project_image_url}
                      alt={current.project_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="font-bold uppercase text-secondary md:text-8xl">
                      {current.client_name.substring(0, 2)}
                    </span>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="basis-96">
              <div className="mb-4 font-bold text-secondary">{current.client_name}</div>
              <div className="mb-3 text-secondary">{current.project_name}</div>

              <div className="mb-3 hidden text-secondary md:block">
                {current.project_description}
              </div>
            </div>
            <div className="md:flex md:flex-shrink-0 md:flex-col">
              <div className="mb-10 flex items-center md:h-full md:flex-col md:justify-center md:gap-2">
                {current.services.map((service, index) => (
                  <span
                    key={`${service.id}-${index}`}
                    className={`flex items-center justify-center w-full p-2 bg-transparent border border-secondary-light border-opacity-40 rounded-full focus:outline-none font-ambit text-secondary gap-1 font-bold md:w-auto md:py-1 md:px-12 ${index === 0 ? 'flex' : 'hidden md:flex'}`}
                  >
                    {resolveServiceCategoryLabel(service, i18n.language, t)}
                  </span>
                ))}
                {current.services.length > 1 && (
                  <button className="ml-4 rounded-full border border-secondary-light border-opacity-50 px-3 py-2 text-secondary md:hidden">
                    {current.services.length - 1}+
                  </button>
                )}
              </div>
              <LeftRightNavigationButtons
                hasPrev={hasPrev}
                hasNext={hasNext}
                onPrev={() => hasPrev && swipeTo(currentIndex - 1)}
                onNext={() => hasNext && swipeTo(currentIndex + 1)}
                className="md:b-0 md:self-end md:justify-self-end"
              />
            </div>
          </div>
        ) : (
          <div className="mt-8 text-center text-sm text-secondary">{t('noAchievements')}</div>
        )}
      </CollapsibleContent>
    </div>
  );
}

export default ProfileAchievementsSection;
